import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { EventForm } from '@/features/community/EventForm';
import { useDeleteEvent, useEvents } from '@/queries/community';
import { useApiError } from '@/lib/useApiError';
import { useUiStore } from '@/stores/ui';
import type { CommunityEvent, EventState } from '@/schemas/admin/community/events';

export const Route = createFileRoute('/_authed/community/events')({
  component: EventsScreen,
});

function stateTone(state: EventState): 'neutral' | 'info' | 'success' | 'warn' | 'danger' {
  switch (state) {
    case 'live':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'completed':
      return 'neutral';
    case 'cancelled':
      return 'danger';
    default:
      return 'warn';
  }
}

function EventsScreen() {
  const { t } = useTranslation('common');
  const locale = useUiStore((s) => s.locale);
  const list = useEvents();
  const del = useDeleteEvent();
  const resolveError = useApiError();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<CommunityEvent | null>(null);
  const [deleting, setDeleting] = React.useState<CommunityEvent | null>(null);
  const [stateFilter, setStateFilter] = React.useState<EventState | 'all'>('all');

  function displayTitle(e: CommunityEvent): string {
    if (locale === 'eg-ar') return e.title_eg_ar ?? e.title_en ?? e.slug;
    if (locale === 'ar') return e.title_ar ?? e.title_en ?? e.slug;
    return e.title_en ?? e.slug;
  }

  const filtered = React.useMemo(() => {
    const data = list.data ?? [];
    if (stateFilter === 'all') return data;
    return data.filter((e) => e.state === stateFilter);
  }, [list.data, stateFilter]);

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(t('actions.delete', { defaultValue: 'Deleted' }));
      setDeleting(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<CommunityEvent>[] = [
    {
      id: 'title',
      header: 'Title',
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{displayTitle(r)}</span>
          <span dir="ltr" className="font-mono text-[11px] text-[color:var(--color-fg-muted)]">
            {r.slug}
          </span>
        </div>
      ),
      sortBy: (r) => displayTitle(r),
    },
    {
      id: 'state',
      header: 'State',
      cell: (r) => <Badge tone={stateTone(r.state)}>{r.state}</Badge>,
      sortBy: (r) => r.state,
    },
    {
      id: 'starts',
      header: 'Starts',
      cell: (r) => <DateTime value={r.starts_at} />,
      sortBy: (r) => (r.starts_at ? new Date(r.starts_at) : null),
    },
    {
      id: 'venue',
      header: 'Venue',
      cell: (r) => {
        const parts = [r.venue, r.city, r.country].filter(Boolean).join(' · ');
        return parts || <span className="text-[color:var(--color-fg-subtle)]">—</span>;
      },
    },
    {
      id: 'rsvp',
      header: 'RSVPs',
      cell: (r) =>
        typeof r.rsvp_count === 'number' ? (
          <span dir="ltr" className="font-mono">
            {r.rsvp_count}
            {typeof r.capacity === 'number' ? ` / ${r.capacity}` : ''}
          </span>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
      align: 'end',
      sortBy: (r) => r.rsvp_count ?? -1,
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label={`Edit ${displayTitle(r)}`}>
            <Pencil aria-hidden className="size-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} aria-label={`Delete ${displayTitle(r)}`}>
            <Trash2 aria-hidden className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Events</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">
            Editorial events surfaced on the consumer Community page. Pillar tagging targets the content-mix dial; chapter or group links scope the surface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            New event
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="events-state" className="text-xs font-medium text-[color:var(--color-fg-muted)]">
              State
            </label>
            <select
              id="events-state"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as EventState | 'all')}
              dir="ltr"
              className="h-9 rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
            >
              <option value="all">All</option>
              {(['draft', 'scheduled', 'live', 'completed', 'cancelled'] as const).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <DataTable<CommunityEvent>
            data={filtered}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: 'No events yet.', body: 'Schedule a meetup to get started.' }}
            initialSort={{ id: 'starts', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New event</DialogTitle>
            <DialogDescription>
              Slug is immutable once saved. End time must be on or after the start.
            </DialogDescription>
          </DialogHeader>
          <EventForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit event</DialogTitle>
            <DialogDescription>Update event copy, schedule, venue, or state.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <EventForm
              event={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete event?"
        description={deleting ? `Permanently remove "${displayTitle(deleting)}". RSVPs will be cleared.` : ''}
        destructive
        requireType="DELETE"
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
